import {
  IActionExportQueuedResponse,
  IActionParams,
  IActionPreviewResponse,
  IActionsResponse,
  IUpcomingActionsSummaryResponse,
  PolicyType,
} from 'shared';

import { CacheTags } from '../../types/cacheTags';
import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { emptySplitApi } from '../baseApi';

export const actionApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchActions: builder.query<IActionsResponse, IActionParams>({
      query: ({ organisationId, ...params }) => ({
        url: `actions`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params: {
          ...params,
        },
      }),
      providesTags: result => result ?
        [
          ...result.actions.map(({ id }) => ({ type: CacheTags.Actions, id })),
          { type: CacheTags.Actions, id: `LIST` },
        ]
        : [{ type: CacheTags.Actions, id: `LIST` }],
    }),
    exportAllActions: builder.mutation<IActionExportQueuedResponse, IActionParams & { filename?: string; columns?: string[] }>({
      query: ({ organisationId, ...params }) => ({
        url: `actions/export`,
        method: `POST`,
        params: {
          ...params,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),
    // Actions for dash widget - initially only the draft ones due today
    fetchDashboardActions: builder.query<IActionsResponse, IActionParams>({
      query: ({ organisationId, ...params }) => ({
        url: `actions`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params: {
          ...params,
        },
      }),
      providesTags: result => result ?
        [
          ...result.actions.map(({ id }) => ({ type: CacheTags.DashActions, id })),
          { type: CacheTags.DashActions, id: `LIST` },
        ]
        : [{ type: CacheTags.DashActions, id: `LIST` }],
    }),
    // Aggregated counts for the dashboard upcoming actions widget
    fetchUpcomingActionsSummary: builder.query<IUpcomingActionsSummaryResponse, { organisationId: string }>({
      query: ({ organisationId }) => ({
        url: `actions/upcoming-summary`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [{ type: CacheTags.DashActions, id: `SUMMARY` }],
    }),
    getActionById: builder.query<IActionsResponse[`actions`][number], { organisationId: string; actionId: string }>({
      query: ({ organisationId, actionId }) => ({
        url: `actions/${actionId}`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      transformResponse: (response: { action: IActionsResponse[`actions`][number] }) => response.action,
      providesTags: (_result, _error, args) => ([
        { type: CacheTags.Actions, id: args.actionId },
        { type: CacheTags.DashActions, id: args.actionId },
      ]),
    }),
    revertAction: builder.mutation({
      query: ({
        organisationId,
        actionId,
      }) => ({
        url: `actions/${actionId}/revert`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),
    recalculateActions: builder.mutation({
      query: ({
        organisationId,
        invoiceId,
      }) => ({
        url: `actions/recalculate`,
        method: `POST`,
        params: {
          invoiceId,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.DashActions, id: `LIST` },
      ],
    }),
    actionNow: builder.mutation({
      query: ({
        organisationId,
        actionId,
      }) => ({
        url: `actions/${actionId}/now`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.Organisations, id: `LIST` },
        { type: CacheTags.DashActions, id: `LIST` },
      ],
    }),
    retryAction: builder.mutation({
      query: ({
        organisationId,
        actionId,
      }) => ({
        url: `actions/${actionId}/retry`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.Organisations, id: `LIST` },
        { type: CacheTags.DashActions, id: `LIST` },
      ],
    }),
    forceRecheckAll: builder.mutation({
      query: ({
        organisationId,
      }) => ({
        url: `actions/force-recheck`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),
    removeAction: builder.mutation({
      query: ({
        organisationId,
        actionId,
      }) => ({
        url: `actions/${actionId}`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),
    removeAllFailedActions: builder.mutation({
      query: ({
        organisationId,
      }) => ({
        url: `actions`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),
    removeFromBatch: builder.mutation({
      query: ({
        organisationId,
        actionId,
      }) => ({
        url: `actions/${actionId}/remove-from-batch`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),
    /** E.g recreate the late fee email actions */
    regenerateChildActions: builder.mutation({
      query: ({
        organisationId,
        actionId,
      }) => ({
        url: `actions/${actionId}/regenerate-child-actions`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),

    regenerateChildActionsBulk: builder.mutation({
      query: ({
        organisationId,
        actionIds,
      }) => ({
        url: `actions/bulk/regenerateChildActions`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          actionIds,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),

    previewAction: builder.query<IActionPreviewResponse, { organisationId: string; actionId: string }>({
      query: ({ organisationId, actionId }) => ({
        url: `actions/${actionId}/preview`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: (result, error, { actionId }) => [{ type: CacheTags.ActionPreview, id: actionId }],
    }),

    // Clears the cached Intelligent Dynamic email; the preview refetch writes a new one
    regenerateAiEmail: builder.mutation<void, { organisationId: string; actionId: string }>({
      query: ({ organisationId, actionId }) => ({
        url: `actions/${actionId}/ai-email`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: (result, error, { actionId }) => [{ type: CacheTags.ActionPreview, id: actionId }],
    }),

    approveActions: builder.mutation({
      query: ({
        organisationId,
        actionIds,
        force,
      }: {
        organisationId: string;
        actionIds: string[];
        force: boolean;
      }) => ({
        url: `actions/bulk/approve`,
        method: `POST`,
        data: {
          actionIds,
          force,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),

    revertApprovals: builder.mutation({
      query: ({
        organisationId,
        actionIds,
      }: {
        organisationId: string;
        actionIds: string[];
      }) => ({
        url: `actions/bulk/revert-approvals`,
        method: `POST`,
        data: {
          actionIds,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),
    adhocBulkActions: builder.mutation({
      query: ({
        organisationId,
        type,
        invoiceIds,
        contactIds,
        checkChaseable,
      }: {
        organisationId: string;
        type: PolicyType;
        invoiceIds?: string[];
        contactIds?: number[];
        /** Leave out frozen, queried and promised invoices */
        checkChaseable?: boolean;
      }) => ({
        url: `actions/bulk/adhoc`,
        method: `POST`,
        data: {
          type,
          invoiceIds,
          contactIds,
          checkChaseable,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),
    removePendingAdhoc: builder.mutation({
      query: ({
        organisationId,
      }: {
        organisationId: string;
      }) => ({
        url: `actions/bulk/remove-pending`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),
    skipActions: builder.mutation({
      query: ({
        organisationId,
        actionIds,
      }: {
        organisationId: string;
        actionIds: string[];
      }) => ({
        url: `actions/bulk/skip`,
        method: `POST`,
        data: {
          actionIds,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),
    deleteSkippedActions: builder.mutation({
      query: ({
        organisationId,
        actionIds,
      }: {
        organisationId: string;
        actionIds: string[];
      }) => ({
        url: `actions/bulk/delete-skipped`,
        method: `POST`,
        data: {
          actionIds,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Actions, id: `LIST` }],
    }),
  }),
});

export const {
  useFetchActionsQuery,
  useExportAllActionsMutation,
  useGetActionByIdQuery,
  useRecalculateActionsMutation,
  useRevertActionMutation,
  useActionNowMutation,
  useRetryActionMutation,
  useForceRecheckAllMutation,
  useRemoveActionMutation,
  useRemoveAllFailedActionsMutation,
  useRemoveFromBatchMutation,
  useRegenerateChildActionsMutation,
  usePreviewActionQuery,
  useRegenerateAiEmailMutation,
  useFetchDashboardActionsQuery,
  useFetchUpcomingActionsSummaryQuery,
  useApproveActionsMutation,
  useRevertApprovalsMutation,
  useAdhocBulkActionsMutation,
  useRemovePendingAdhocMutation,
  useRegenerateChildActionsBulkMutation,
  useSkipActionsMutation,
  useDeleteSkippedActionsMutation,
} = actionApi;
